import clsx from 'clsx';
import Image from 'next/legacy/image';
import { FC, ReactNode, useEffect, useState } from 'react';

import { BUY_TYPE, FINANCE_QUOTE_REPAYMENT_FREQUENCY } from 'src/checkout/templates/Checkout/Forms/CheckoutForm.types';
import { FINANCE_QUOTE_PROVIDER, FinanceQuote, RefusalError } from 'src/data/FinanceApi/FinanceApi.types';
import { MICROCOPY, MicrocopyKey } from 'src/data/microcopy/microcopyDictionary';
import { Button } from 'src/general/components/Button/Button';
import { Skeleton } from 'src/general/components/Skeleton/Skeleton';
import { carmaGenericImageLoader } from 'src/general/helpers/carmaGenericImageLoader';
import { formatNumber } from 'src/general/helpers/formatNumber';
import { getMicrocopy, getMicrocopyBulk } from 'src/stores/microcopyStore';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { FinanceQuoteBreakdownModalLegacy } from '../FinanceQuoteBreakdownModal/FinanceQuoteBreakdownModalLegacy';
import { VWFSModal } from '../VWFSModal/VWFSModal';
import styles from './FinanceQuoteOption.module.scss';

export const providerData: {
  [key in Exclude<FINANCE_QUOTE_PROVIDER, FINANCE_QUOTE_PROVIDER.CARMA>]: {
    provider: FINANCE_QUOTE_PROVIDER;
    image: string;
    businessUnavailable: MicrocopyKey;
    personalUnavailable: MicrocopyKey;
    visaExpiryError: MicrocopyKey;
    visaCodeError: MicrocopyKey;
    foreignError: MicrocopyKey;
    businessError: MicrocopyKey;
    visaIssueLabel: MicrocopyKey;
  };
} = {
  [FINANCE_QUOTE_PROVIDER.ALLIED]: {
    provider: FINANCE_QUOTE_PROVIDER.ALLIED,
    image: '/images/components/CheckoutStep7/automotive.svg',
    businessUnavailable: MICROCOPY.CHECKOUT_FINANCE_QUOTES_ALLIED_UNAVAILABLE,
    personalUnavailable: MICROCOPY.CHECKOUT_FINANCE_QUOTES_ALLIED_UNAVAILABLE,
    visaExpiryError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_EXPIRY_ALLIED_LABEL,
    visaCodeError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_INVALID_VISA_CODE_ALLIED_LABEL,
    foreignError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_FOREIGN_NOT_SUPPORT_ALLIED_LABEL,
    businessError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_BUSINESS_NOT_SUPPORT_ALLIED_LABEL,
    visaIssueLabel: MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_ISSUE_ALLIED_LABEL,
  },
  [FINANCE_QUOTE_PROVIDER.PLENTI]: {
    provider: FINANCE_QUOTE_PROVIDER.PLENTI,
    image: '/images/components/CheckoutStep7/plenti.svg',
    businessUnavailable: MICROCOPY.CHECKOUT_FINANCE_QUOTES_PLENTI_BUSINESS_UNAVAILABLE,
    personalUnavailable: MICROCOPY.CHECKOUT_FINANCE_QUOTES_PLENTI_PERSONAL_UNAVAILABLE,
    visaExpiryError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_EXPIRY_PLENTI_LABEL,
    visaCodeError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_INVALID_VISA_CODE_PLENTI_LABEL,
    foreignError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_FOREIGN_NOT_SUPPORT_PLENTI_LABEL,
    businessError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_BUSINESS_NOT_SUPPORT_PLENTI_LABEL,
    visaIssueLabel: MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_ISSUE_PLENTI_LABEL,
  },
  [FINANCE_QUOTE_PROVIDER.WISR]: {
    provider: FINANCE_QUOTE_PROVIDER.WISR,
    image: '/images/components/CheckoutStep7/wisr.svg',
    businessUnavailable: MICROCOPY.CHECKOUT_FINANCE_QUOTES_WISR_BUSINESS_UNAVAILABLE,
    personalUnavailable: MICROCOPY.CHECKOUT_FINANCE_QUOTES_WISR_PERSONAL_UNAVAILABLE,
    visaExpiryError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_EXPIRY_WISR_LABEL,
    visaCodeError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_INVALID_VISA_CODE_WISR_LABEL,
    foreignError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_FOREIGN_NOT_SUPPORT_WISR_LABEL,
    businessError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_BUSINESS_NOT_SUPPORT_WISR_LABEL,
    visaIssueLabel: MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_ISSUE_WISR_LABEL,
  },
  [FINANCE_QUOTE_PROVIDER.VWFS]: {
    provider: FINANCE_QUOTE_PROVIDER.VWFS,
    image: '/images/components/CheckoutStep7/volkswagen.svg',
    businessUnavailable: MICROCOPY.CHECKOUT_FINANCE_QUOTES_VOLKSWAGEN_UNAVAILABLE,
    personalUnavailable: MICROCOPY.CHECKOUT_FINANCE_QUOTES_VOLKSWAGEN_UNAVAILABLE,
    visaExpiryError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_EXPIRY_VW_LABEL,
    visaCodeError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_INVALID_VISA_CODE_VW_LABEL,
    foreignError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_FOREIGN_NOT_SUPPORT_VW_LABEL,
    businessError: MICROCOPY.CHECKOUT_FINANCE_QUOTE_BUSINESS_NOT_SUPPORT_VW_LABEL,
    visaIssueLabel: MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_ISSUE_VW_LABEL,
  },
};

export const enum FINANCE_QUOTE_OPTION_STATUS {
  LOADING = 'loading',
  UNAVAILABLE = 'unavailable',
  AVAILABLE = 'available',
  DENIED = 'denied',
}

const enum FINANCE_QUOTE_OPTION_ERRORS_TYPE {
  FOREIGN_NOT_SUPPORT = 'foreign_resident_not_supported',
  BUSINESS_NOT_SUPPORT = 'business_application_not_supported',
  VISA_EXPIRY = 'invalid_visa_expiry',
  VISA_CODE = 'invalid_visa_code',
  LOAN_AMOUNT_CAPPED = 'loan_amount_capped',
}

type Props = Prettify<
  {
    provider: Exclude<FINANCE_QUOTE_PROVIDER, FINANCE_QUOTE_PROVIDER.CARMA>;
    buyType: BUY_TYPE;
    frequency: FINANCE_QUOTE_REPAYMENT_FREQUENCY;
    carPrice: number;
    deposit: number;
    isSupportedByYouX?: boolean;
    make?: string;
  } & (
    | {
      status: FINANCE_QUOTE_OPTION_STATUS.LOADING | FINANCE_QUOTE_OPTION_STATUS.UNAVAILABLE;
    }
    | {
      status: FINANCE_QUOTE_OPTION_STATUS.DENIED;
      refusal?: RefusalError[] | null;
    }
    | {
      status: FINANCE_QUOTE_OPTION_STATUS.AVAILABLE;
      quote: FinanceQuote;
    }
  )
>;

// FinanceQuoteOptionLegacy is being used in personalized-quote
export const FinanceQuoteOption: FC<Props> = (props) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [vwfsModalOpen, setVWFSModalOpen] = useState<boolean>(false);
  const breakdownButtonLabel = getMicrocopy(MICROCOPY.CHECKOUT_FINANCE_QUOTE_FEE_BREAKDOWN_CTA);
  const visaIssueLabel = getMicrocopy(providerData[props.provider].visaIssueLabel);
  const unavailableLabel = getMicrocopy(MICROCOPY.CHECKOUT_FINANCE_QUOTE_UNAVAILABLE_LABEL);
  const unavailableCopy = getMicrocopy(
    props.buyType === BUY_TYPE.PERSONAL
      ? providerData[props.provider].personalUnavailable
      : providerData[props.provider].businessUnavailable,
    true,
  );
  const errorMessageLabel = (): ReactNode => {
    if (props.isSupportedByYouX) {
      return visaIssueLabel;
    }

    if (props.status === FINANCE_QUOTE_OPTION_STATUS.DENIED) {
      const checkError = props.refusal?.[0];
      if (checkError) {
        switch (checkError.key) {
          case FINANCE_QUOTE_OPTION_ERRORS_TYPE.VISA_EXPIRY:
            return getMicrocopy(providerData[props.provider].visaExpiryError);
          case FINANCE_QUOTE_OPTION_ERRORS_TYPE.VISA_CODE:
            return getMicrocopy(providerData[props.provider].visaCodeError);
          case FINANCE_QUOTE_OPTION_ERRORS_TYPE.FOREIGN_NOT_SUPPORT:
            return getMicrocopy(providerData[props.provider].foreignError);
          case FINANCE_QUOTE_OPTION_ERRORS_TYPE.BUSINESS_NOT_SUPPORT:
            return getMicrocopy(providerData[props.provider].businessError);
          case FINANCE_QUOTE_OPTION_ERRORS_TYPE.LOAN_AMOUNT_CAPPED:
            return 'This quote is subject to certain conditions. Get in touch with our team for full details.';
          default:
            return checkError.message;
        }
      }
    }
    return unavailableCopy;
  };

  const exitFeeMicrocopies = getMicrocopyBulk({
    [`${FINANCE_QUOTE_PROVIDER.ALLIED}_${BUY_TYPE.PERSONAL}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_ALLIED_PERSONAL_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.ALLIED}_${BUY_TYPE.BUSINESS}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_ALLIED_BUSINESS_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.PLENTI}_${BUY_TYPE.PERSONAL}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_PLENTI_PERSONAL_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.PLENTI}_${BUY_TYPE.BUSINESS}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_PLENTI_BUSINESS_EARLY_TERMINATION_FEE,
      true,
    ],
    [`${FINANCE_QUOTE_PROVIDER.WISR}_${BUY_TYPE.PERSONAL}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_WISR_PERSONAL_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.WISR}_${BUY_TYPE.BUSINESS}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_WISR_BUSINESS_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.VWFS}_${BUY_TYPE.PERSONAL}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_VWFS_PERSONAL_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.VWFS}_${BUY_TYPE.BUSINESS}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_VWFS_BUSINESS_EARLY_TERMINATION_FEE,
    ],
  });

  const terminationFeeLabels = getMicrocopyBulk({
    [`${FINANCE_QUOTE_PROVIDER.ALLIED}_${BUY_TYPE.PERSONAL}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTES_ALLIED_PERSONAL_HAS_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.ALLIED}_${BUY_TYPE.BUSINESS}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTES_ALLIED_BUSINESS_HAS_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.PLENTI}_${BUY_TYPE.PERSONAL}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTES_PLENTI_PERSONAL_HAS_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.PLENTI}_${BUY_TYPE.BUSINESS}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTES_PLENTI_BUSINESS_HAS_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.WISR}_${BUY_TYPE.PERSONAL}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTES_WISR_PERSONAL_HAS_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.WISR}_${BUY_TYPE.BUSINESS}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTES_WISR_BUSINESS_HAS_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.VWFS}_${BUY_TYPE.PERSONAL}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTES_VW_PERSONAL_HAS_EARLY_TERMINATION_FEE,
    ],
    [`${FINANCE_QUOTE_PROVIDER.VWFS}_${BUY_TYPE.BUSINESS}`]: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTES_VW_BUSINESS_HAS_EARLY_TERMINATION_FEE,
    ],
  });

  useEffect(() => {
    if (modalOpen) {
      pushToDataLayer({
        event: `checkout_click`,
        value: `fee_breakdown`,
        provider: props.provider,
      });
    }
  }, [modalOpen]);

  if (props.status === FINANCE_QUOTE_OPTION_STATUS.AVAILABLE && props.quote.offer.fees) {
    const weeklyMultiplier = props.frequency === FINANCE_QUOTE_REPAYMENT_FREQUENCY.WEEKLY ? 12 / 52 : 1;

    const repayment = formatNumber((props.quote.offer.repayment_amount_cents / 100) * weeklyMultiplier, {
      prepend: '$',
      decimals: 2,
    });
    const repaymentPerMonth = formatNumber(props.quote.offer.repayment_amount_cents / 100, {
      prepend: '$',
      decimals: 2,
    });
    const interestRate = formatNumber(props.quote.offer.rate * 100, { append: '%', decimals: 2 });
    const establishmentFees = formatNumber(
      (props.quote.offer.fees.establishment_fee_cents + props.quote.offer.fees.ppsr_fee_cents) / 100,
      {
        prepend: '$',
        decimals: 2,
        trimZeroDecimals: true,
      },
    );
    const purchasePrice = formatNumber(props.carPrice, {
      prepend: '$',
      decimals: 2,
      trimZeroDecimals: true,
    });
    const totalDeposit = formatNumber(props.deposit, {
      prepend: '$',
      decimals: 2,
      trimZeroDecimals: true,
    });

    const includedFinanceFeesCents =
      props.quote.offer.fees.establishment_fee_cents +
      props.quote.offer.fees.broker_fee_cents +
      props.quote.offer.fees.ppsr_fee_cents;

    // https://app.clickup.com/t/6937126/TECH-13703
    // Fees to be included to the Total amount financed are:
    // Establishment fee + PPSR fee + Introducer Fee
    const totalAmountFinanced = formatNumber(Number(props.carPrice) + includedFinanceFeesCents / 100 - props.deposit, {
      prepend: '$',
      decimals: 2,
      trimZeroDecimals: true,
    });
    const establishmentFee = formatNumber(props.quote.offer.fees.establishment_fee_cents / 100, {
      prepend: '$',
      decimals: 2,
      trimZeroDecimals: true,
    });
    const monthlyFees = formatNumber(props.quote.offer.fees.repayment_fee_cents / 100, {
      prepend: '$',
      decimals: 2,
      trimZeroDecimals: true,
    });
    const introducerFee = formatNumber(props.quote.offer.fees.broker_fee_cents / 100, {
      prepend: '$',
      decimals: 2,
      trimZeroDecimals: true,
    });
    const ppsrFee = formatNumber(props.quote.offer.fees.ppsr_fee_cents / 100, {
      prepend: '$',
      decimals: 2,
      trimZeroDecimals: true,
    });

    return (
      <>
        <div className={clsx(styles.root, styles.available)}>
          <div className={styles.quote}>
            <div className={clsx('c-fs-h5', styles.quoteAmount)}>
              {repayment}
              <sub>&nbsp;/ {props.frequency === FINANCE_QUOTE_REPAYMENT_FREQUENCY.WEEKLY ? 'week' : 'month'} *</sub>
            </div>
            <div
              className={clsx('c-fs-h5', styles.quoteRate)}
              data-testid={`finance-quote-interest-rate-${props.provider}`}
            >
              {interestRate}
              <sub>&nbsp;interest rate p.a.</sub>
            </div>
          </div>
          <div className={styles.logo} data-testid={`finance-quote-logo-${props.provider}`}>
            <Image
              src={providerData[props.provider].image}
              width={84}
              height={29}
              layout="fixed"
              alt={props.provider}
              loader={carmaGenericImageLoader}
            />
          </div>
          <dl className={styles.fees}>
            <dt>Establishment fees</dt>
            <dd>{establishmentFees}</dd>
            <dt>Monthly fee</dt>
            <dd>{monthlyFees}</dd>
            <dt>Early termination fee</dt>
            <dd>{terminationFeeLabels[`${props.provider}_${props.buyType}`]}</dd>
          </dl>
          <div className={styles.breakdown}>
            <Button variant="link" size="small" onClick={() => setModalOpen(true)}>
              {breakdownButtonLabel}
            </Button>
          </div>
        </div>
        <FinanceQuoteBreakdownModalLegacy
          id={props.provider}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          image={providerData[props.provider].image}
          interestRate={interestRate}
          repaymentPerMonth={repaymentPerMonth}
          purchasePrice={purchasePrice}
          totalDeposit={totalDeposit}
          establishmentFee={establishmentFee}
          introducerFee={introducerFee}
          ppsrFee={ppsrFee}
          providerMonthlyFee={monthlyFees}
          totalAmountFinanced={totalAmountFinanced}
          earlyTerminationFee={exitFeeMicrocopies[`${props.provider}_${props.buyType}`] as string}
          financier={props.provider}
        />
        <VWFSModal isOpen={vwfsModalOpen} onClose={() => setVWFSModalOpen(false)} />
      </>
    );
  }

  if (props.status === FINANCE_QUOTE_OPTION_STATUS.UNAVAILABLE || props.status === FINANCE_QUOTE_OPTION_STATUS.DENIED) {
    const isCappedQuote =
      props.status === FINANCE_QUOTE_OPTION_STATUS.DENIED &&
      props.refusal &&
      props.refusal?.[0].key === FINANCE_QUOTE_OPTION_ERRORS_TYPE.LOAN_AMOUNT_CAPPED;
    return (
      <div className={clsx(styles.root, styles.unavailable, 'is-unavailable')}>
        <div className={styles.quote}>{isCappedQuote ? 'Conditioned Quote' : unavailableLabel}</div>
        <div className={styles.logo}>
          <Image
            src={providerData[props.provider].image}
            width={84}
            height={29}
            layout="fixed"
            alt={props.provider}
            loader={carmaGenericImageLoader}
          />
        </div>
        <div className={styles.fees}>{errorMessageLabel()}</div>
        <div className={styles.breakdown} />
      </div>
    );
  }

  // Defaults to loading
  return (
    <div className={clsx(styles.root, styles.loading, 'is-loading')}>
      <div className={styles.quote}>
        <div className={styles.quoteAmountSkeleton}>
          <Skeleton />
        </div>
        <div className={styles.quoteRateSkeleton}>
          <Skeleton />
        </div>
      </div>
      <div className={styles.logo} />
      <dl className={styles.feesSkeleton}>
        <dt>
          <Skeleton />
        </dt>
        <dd>
          <Skeleton />
        </dd>
        <dt>
          <Skeleton />
        </dt>
        <dd>
          <Skeleton />
        </dd>
        <dt>
          <Skeleton />
        </dt>
        <dd>
          <Skeleton />
        </dd>
      </dl>
      <div className={styles.breakdown} />
    </div>
  );
};
